import moment from "moment";
import { Controller, type UseFormReturn } from "react-hook-form";
import { type z } from "zod";

import { useUser } from "@components/user-context";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { CurrencyInput } from "@shared/currency-input";

import { type ClientSettingSchema } from "./client-setting-form";

type Props = {
  formMeta: UseFormReturn<z.infer<typeof ClientSettingSchema>>;
  settingStartDate: string;
  settingEndDate: string;
  settingPeriodLength: number;
  previousPeriodSpendData: {
    totalSpend: number;
    perChannelSpend: Record<string, number>;
  };
};

export const SettingBudgetsField = ({
  formMeta,
  settingStartDate,
  settingEndDate,
  settingPeriodLength,
  previousPeriodSpendData,
}: Props) => {
  const { user } = useUser();

  return (
    <div className="border-b border-v2-secondary px-6 pt-5 pb-8">
      <label className="block font-semibold">
        3. Set a budget for
        {` ${moment(settingStartDate).format("MMM. DD")} - ${moment(
          settingEndDate,
        ).format("MMM. DD YYYY")}`}{" "}
        ({settingPeriodLength} days)
      </label>
      <p className="text-sm mb-5">
        Enter values below to generate optimized efficiency mixes.
      </p>
      <div className="flex items-start  justify-between gap-1">
        <Controller
          control={formMeta.control}
          name="budget_low"
          render={({ field: { value, onChange } }) => (
            <CurrencyInput
              label="MINIMUM BUDGET"
              className="w-[14rem]"
              inputClassName="text-right"
              errorClassName="static mt-1"
              errorMessage={formMeta.formState.errors.budget_low?.message}
              min={0}
              code={user.currencies.currentCurrency}
              onChange={onChange}
              value={value}
            />
          )}
        />
        <div>
          <Controller
            control={formMeta.control}
            name="target_spend"
            render={({ field: { value, onChange } }) => (
              <CurrencyInput
                label="IDEAL TARGET BUDGET"
                className="w-[14rem]"
                inputClassName="text-right"
                errorClassName="static mt-1"
                errorMessage={formMeta.formState.errors.target_spend?.message}
                min={0}
                code={user.currencies.currentCurrency}
                onChange={onChange}
                value={value}
              />
            )}
          />
          <div className="text-xxs text-gray+1 mt-1">
            Last period&apos;s spend:{" "}
            {MetricFormatters.currency(
              previousPeriodSpendData.totalSpend,
              user.currencies.currentCurrency,
              { precision: "exact" },
            )}
          </div>
        </div>
        <Controller
          control={formMeta.control}
          name="budget_high"
          render={({ field: { value, onChange } }) => (
            <CurrencyInput
              label="MAXIMUM BUDGET"
              className="w-[14rem]"
              inputClassName="text-right"
              errorClassName="static mt-1"
              errorMessage={formMeta.formState.errors.budget_high?.message}
              min={0}
              code={user.currencies.currentCurrency}
              onChange={onChange}
              value={value}
            />
          )}
        />
      </div>
    </div>
  );
};
